import { useState, useCallback, useEffect } from 'react';
import { Card, Button, Input, Select, LoadingSpinner } from '../ui';
import {
  PlusIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import { useCreateDatabase, useUpdateDatabase, useInferCSVSchema, useInferCSVPair, useDatabaseSchema } from '../../hooks/useDatabases';
import ColumnMappingList from './ColumnMappingList';

const COLUMN_TYPES = ['number', 'scale', 'likert-group', 'boolean', 'enum', 'multi-enum', 'text'];
const FORMULA_TYPES = ['average', 'normalize-sum', 'percentage-where', 'average-mapped', 'expression', 'count-where'];
const DEFAULT_COLORS = ['#10b981', '#8b5cf6', '#3b82f6', '#f59e0b', '#06b6d4', '#ef4444', '#ec4899', '#14b8a6'];

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function makeEmptyColumn() {
  return { key: '', label: '', type: 'number', required: false, surveySection: '', surveyLabel: '', isDemographic: false };
}

function makeEmptyMetric() {
  return { key: '', label: '', formula: 'average', sourceColumn: '', unit: '', decimals: 1, displayScale: { min: 0, max: 100 } };
}

export default function DatabaseEditor({ database, onSave, onCancel }) {
  const isEditing = !!database?.id;
  const createDb = useCreateDatabase();
  const updateDb = useUpdateDatabase();
  const inferCSV = useInferCSVSchema();
  const inferCSVPair = useInferCSVPair();
  const { data: fullSchema, isLoading: schemaLoading } = useDatabaseSchema(database?.id);

  const [name, setName] = useState(database?.name || '');
  const [description, setDescription] = useState(database?.description || '');
  const [isDefault, setIsDefault] = useState(database?.isDefault || false);
  const [kind, setKind] = useState(database?.kind || 'single');
  const [columns, setColumns] = useState(database?.columns || []);
  const [metrics, setMetrics] = useState(database?.metrics || []);
  // Paired-mode state
  const [preColumns, setPreColumns] = useState(database?.pre?.columns || []);
  const [preMetrics, setPreMetrics] = useState(database?.pre?.metrics || []);
  const [postColumns, setPostColumns] = useState(database?.post?.columns || []);
  const [postMetrics, setPostMetrics] = useState(database?.post?.metrics || []);
  const [columnMapping, setColumnMapping] = useState(database?.columnMapping || []);
  const [idColumn, setIdColumn] = useState(database?.idColumn || null);
  const [matchingStrategy, setMatchingStrategy] = useState(database?.matchingStrategy || 'cookie');
  const [layout, setLayout] = useState(database?.dashboardLayout || {
    overviewCards: [], timeSeriesMetrics: [], comparisonMetrics: [], radarMetrics: [], maxDiffCharts: [],
  });
  const [expandedColumn, setExpandedColumn] = useState(null);
  const [expandedMetric, setExpandedMetric] = useState(null);
  const [activeSide, setActiveSide] = useState('pre');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Populate state once full schema loads (list endpoint only returns summary)
  useEffect(() => {
    if (fullSchema) {
      setName(fullSchema.name || '');
      setDescription(fullSchema.description || '');
      setIsDefault(fullSchema.isDefault || false);
      setKind(fullSchema.kind || 'single');
      setColumns(fullSchema.columns || []);
      setMetrics(fullSchema.metrics || []);
      setPreColumns(fullSchema.pre?.columns || []);
      setPreMetrics(fullSchema.pre?.metrics || []);
      setPostColumns(fullSchema.post?.columns || []);
      setPostMetrics(fullSchema.post?.metrics || []);
      setColumnMapping(fullSchema.columnMapping || []);
      setIdColumn(fullSchema.idColumn || null);
      setMatchingStrategy(fullSchema.matchingStrategy || 'cookie');
      setLayout(fullSchema.dashboardLayout || {
        overviewCards: [], timeSeriesMetrics: [], comparisonMetrics: [], radarMetrics: [], maxDiffCharts: [],
      });
    }
  }, [fullSchema]);

  const slug = slugify(name);

  // Effective state: swaps between single-mode and paired-mode (pre/post) based on activeSide
  const effectiveColumns = kind === 'paired' ? (activeSide === 'pre' ? preColumns : postColumns) : columns;
  const effectiveMetrics = kind === 'paired' ? (activeSide === 'pre' ? preMetrics : postMetrics) : metrics;
  const setEffectiveColumns = useCallback((updater) => {
    if (kind === 'paired') {
      (activeSide === 'pre' ? setPreColumns : setPostColumns)(updater);
    } else {
      setColumns(updater);
    }
  }, [kind, activeSide]);
  const setEffectiveMetrics = useCallback((updater) => {
    if (kind === 'paired') {
      (activeSide === 'pre' ? setPreMetrics : setPostMetrics)(updater);
    } else {
      setMetrics(updater);
    }
  }, [kind, activeSide]);

  const columnKeys = effectiveColumns.filter(c => c.key).map(c => c.key);
  const metricKeys = effectiveMetrics.filter(m => m.key).map(m => m.key);

  // --- Column helpers ---
  const updateColumn = useCallback((index, updates) => {
    setEffectiveColumns(prev => prev.map((col, i) => i === index ? { ...col, ...updates } : col));
  }, [setEffectiveColumns]);

  const removeColumn = useCallback((index) => {
    setEffectiveColumns(prev => prev.filter((_, i) => i !== index));
    setExpandedColumn(null);
  }, [setEffectiveColumns]);

  const moveColumn = useCallback((index, direction) => {
    setEffectiveColumns(prev => {
      const arr = [...prev];
      const target = index + direction;
      if (target < 0 || target >= arr.length) return arr;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      return arr;
    });
  }, [setEffectiveColumns]);

  // --- Metric helpers ---
  const updateMetric = useCallback((index, updates) => {
    setEffectiveMetrics(prev => prev.map((m, i) => i === index ? { ...m, ...updates } : m));
  }, [setEffectiveMetrics]);

  const removeMetric = useCallback((index) => {
    setEffectiveMetrics(prev => prev.filter((_, i) => i !== index));
    setExpandedMetric(null);
  }, [setEffectiveMetrics]);

  // --- CSV infer ---
  const generateDefaultMetrics = (cols) => {
    return cols.filter(c => !c.isDemographic && c.key).map(col => {
      if (col.type === 'number' || col.type === 'scale') {
        return {
          key: `avg${col.key.charAt(0).toUpperCase()}${col.key.slice(1)}`,
          label: col.label || col.key,
          formula: 'average',
          sourceColumn: col.key,
          unit: col.max ? `/${col.max}` : '',
          decimals: 1,
          displayScale: { min: col.min ?? 0, max: col.max ?? 100 },
        };
      }
      if (col.type === 'boolean') {
        return {
          key: `pct${col.key.charAt(0).toUpperCase()}${col.key.slice(1)}`,
          label: col.label || col.key,
          formula: 'percentage-where',
          condition: { field: col.key, op: '==', value: true },
          unit: '%',
          decimals: 1,
          displayScale: { min: 0, max: 100 },
        };
      }
      if (col.type === 'enum') {
        return {
          key: `avg${col.key.charAt(0).toUpperCase()}${col.key.slice(1)}`,
          label: col.label || col.key,
          formula: 'average-mapped',
          sourceColumn: col.key,
          valueMap: Object.fromEntries((col.options || []).map((opt, i, arr) => [opt, Math.round((i / Math.max(arr.length - 1, 1)) * 100)])),
          unit: '/100',
          decimals: 1,
          displayScale: { min: 0, max: 100 },
        };
      }
      if (col.type === 'likert-group') {
        return {
          key: `avg${col.key.charAt(0).toUpperCase()}${col.key.slice(1)}`,
          label: col.label || col.key,
          formula: 'normalize-sum',
          sourceColumn: col.key,
          normalizeFrom: {
            min: (col.subFields?.length || 1) * (col.likertOptions?.[0]?.value ?? 1),
            max: (col.subFields?.length || 1) * (col.likertOptions?.[col.likertOptions.length - 1]?.value ?? 5),
          },
          normalizeTo: { min: 0, max: 100 },
          unit: '/100',
          decimals: 1,
          displayScale: { min: 0, max: 100 },
        };
      }
      return null;
    }).filter(Boolean);
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await inferCSV.mutateAsync(file);
      if (result.columns) {
        setEffectiveColumns(result.columns);
        setEffectiveMetrics(generateDefaultMetrics(result.columns));
      }
    } catch (err) {
      setError('Failed to infer schema from CSV: ' + (err.response?.data?.error || err.message));
    }
    e.target.value = '';
  };

  // --- Save ---
  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const data = {
        name: name.trim(),
        slug,
        description: description.trim(),
        isDefault,
        kind,
        dashboardLayout: layout,
      };
      if (kind === 'paired') {
        data.pre = { columns: preColumns, metrics: preMetrics };
        data.post = { columns: postColumns, metrics: postMetrics };
        data.columnMapping = columnMapping;
        data.idColumn = idColumn;
        data.matchingStrategy = matchingStrategy;
      } else {
        data.columns = columns;
        data.metrics = metrics;
      }
      if (isEditing) {
        await updateDb.mutateAsync({ id: database.id, ...data });
      } else {
        await createDb.mutateAsync(data);
      }
      onSave(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // --- Layout helpers ---
  const toggleLayoutMetric = (section, key) => {
    setLayout(prev => {
      const arr = prev[section] || [];
      if (section === 'overviewCards' || section === 'timeSeriesMetrics') {
        const existing = arr.findIndex(item => item.metricKey === key);
        if (existing >= 0) {
          return { ...prev, [section]: arr.filter((_, i) => i !== existing) };
        }
        const color = DEFAULT_COLORS[arr.length % DEFAULT_COLORS.length];
        return { ...prev, [section]: [...arr, { metricKey: key, color }] };
      }
      // comparisonMetrics, radarMetrics: simple string arrays
      if (arr.includes(key)) {
        return { ...prev, [section]: arr.filter(k => k !== key) };
      }
      return { ...prev, [section]: [...arr, key] };
    });
  };

  const isLayoutMetricSelected = (section, key) => {
    const arr = layout[section] || [];
    if (section === 'overviewCards' || section === 'timeSeriesMetrics') {
      return arr.some(item => item.metricKey === key);
    }
    return arr.includes(key);
  };

  if (isEditing && schemaLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-text-primary-light dark:text-text-primary">
          {isEditing ? 'Edit Database' : 'New Database'}
        </h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 border border-border rounded-lg p-0.5 bg-surface">
            <button
              onClick={() => setKind('single')}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors cursor-pointer ${kind === 'single' ? 'bg-brand-primary text-white' : 'text-text-secondary hover:opacity-75'}`}
            >
              Single
            </button>
            <button
              onClick={() => setKind('paired')}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors cursor-pointer ${kind === 'paired' ? 'bg-brand-primary text-white' : 'text-text-secondary hover:opacity-75'}`}
            >
              Paired (Pre/Post)
            </button>
          </div>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Basic Info */}
      <Card>
        <h3 className="text-base font-semibold mb-4">Basic Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-1">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. AI Readiness" />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-1">Slug (auto)</label>
            <Input value={slug} readOnly className="opacity-60" />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-brand-primary"
            placeholder="Describe the purpose of this database..."
          />
        </div>
      </Card>

      {kind === 'paired' && (
        <Card>
          <h3 className="text-base font-semibold mb-2">Paired Database Setup</h3>
          <p className="text-xs text-text-muted mb-3">
            A paired database has two sides: Pre (before workshop) and Post (after workshop).
            Configure columns for each side below, then map them in the Column Mapping card.
            One mapping must be marked as the ID column to join responses.
          </p>
          <div>
            <label className="text-xs text-text-muted block mb-1">Bootstrap from two CSVs (optional)</label>
            <div className="flex items-center gap-2">
              <label className="cursor-pointer inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-lg bg-gray-100 dark:bg-background-gray text-text-primary-light dark:text-white border border-border-light dark:border-border hover:opacity-70 transition">
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  multiple
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length < 2) { setError('Select two CSV files (pre and post).'); e.target.value = ''; return; }
                    try {
                      const result = await inferCSVPair.mutateAsync({ preFile: files[0], postFile: files[1] });
                      if (result.pre?.columns) setPreColumns(result.pre.columns);
                      if (result.post?.columns) setPostColumns(result.post.columns);
                      if (result.columnMapping) setColumnMapping(result.columnMapping);
                      if (result.idColumn) setIdColumn(result.idColumn);
                    } catch (err) {
                      setError('Failed to infer paired schemas: ' + (err.response?.data?.error || err.message));
                    }
                    e.target.value = '';
                  }}
                />
                <ArrowUpTrayIcon className="w-4 h-4 mr-1" />
                Upload Pre + Post CSVs
              </label>
              {inferCSVPair.isLoading && <span className="text-xs text-text-muted flex items-center gap-1"><LoadingSpinner /> Inferring...</span>}
            </div>
          </div>
        </Card>
      )}

      {/* CSV Bootstrap */}
      <Card>
        {kind === 'paired' && (
          <div className="border-b border-border-light dark:border-border mb-3 -mt-1">
            <nav className="flex gap-0 -mb-px">
              {['pre', 'post'].map((side) => (
                <button
                  key={side}
                  onClick={() => { setActiveSide(side); setExpandedColumn(null); setExpandedMetric(null); }}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                    activeSide === side
                      ? 'border-brand-primary text-brand-primary'
                      : 'border-transparent text-text-secondary-light dark:text-text-secondary hover:opacity-75'
                  }`}
                >
                  {side === 'pre' ? 'Pre Workshop' : 'Post Workshop'}
                </button>
              ))}
            </nav>
          </div>
        )}
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold">Columns</h3>
          <div className="flex gap-2">
            <label className="cursor-pointer inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-lg bg-gray-100 dark:bg-background-gray text-text-primary-light dark:text-white border border-border-light dark:border-border hover:opacity-70 transition">
              <input type="file" accept=".csv" className="hidden" onChange={handleCSVUpload} />
              <ArrowUpTrayIcon className="w-4 h-4 mr-1" />
              Start from CSV
            </label>
            <Button variant="primary" size="sm" onClick={() => setEffectiveColumns([...effectiveColumns, makeEmptyColumn()])}>
              <PlusIcon className="w-4 h-4 mr-1 inline" />
              Add Column
            </Button>
          </div>
        </div>
        {inferCSV.isLoading && (
          <div className="flex items-center gap-2 py-2 text-sm text-text-muted">
            <LoadingSpinner /> Inferring columns from CSV...
          </div>
        )}
        <p className="text-xs text-text-muted mb-4">Upload a sample CSV to auto-detect columns, or add them manually.</p>

        {effectiveColumns.length === 0 ? (
          <div className="text-center py-8 text-text-muted-light dark:text-text-muted text-sm">
            No columns defined. Add columns manually or upload a CSV.
          </div>
        ) : (
          <div className="space-y-2">
            {effectiveColumns.map((col, i) => (
              <div key={i} className="border border-border rounded-lg">
                {/* Column header row */}
                <div
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                  onClick={() => setExpandedColumn(expandedColumn === i ? null : i)}
                >
                  <span className="text-xs text-text-muted w-6">{i + 1}</span>
                  <input
                    className="flex-1 text-sm bg-transparent border-none outline-none text-text-primary-light dark:text-text-primary"
                    value={col.key}
                    placeholder="key"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updateColumn(i, { key: e.target.value })}
                  />
                  <input
                    className="flex-1 text-sm bg-transparent border-none outline-none text-text-secondary-light dark:text-text-secondary"
                    value={col.label}
                    placeholder="Label"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updateColumn(i, { label: e.target.value })}
                  />
                  <select
                    className="text-xs bg-transparent border border-border rounded px-1 py-0.5 text-text-secondary-light dark:text-text-secondary"
                    value={col.type}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updateColumn(i, { type: e.target.value })}
                  >
                    {COLUMN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <label className="flex items-center gap-1 text-xs" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={col.required || false} onChange={(e) => updateColumn(i, { required: e.target.checked })} />
                    Req
                  </label>
                  <button onClick={(e) => { e.stopPropagation(); moveColumn(i, -1); }} className="p-0.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors" disabled={i === 0}>
                    <ChevronUpIcon className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); moveColumn(i, 1); }} className="p-0.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors" disabled={i === effectiveColumns.length - 1}>
                    <ChevronDownIcon className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); removeColumn(i); }} className="p-0.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors">
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Expanded details */}
                {expandedColumn === i && (
                  <div className="px-3 py-3 border-t border-border bg-neutral-50 dark:bg-neutral-900/50 space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-text-muted">Survey Section</label>
                        <Input size="sm" value={col.surveySection || ''} onChange={(e) => updateColumn(i, { surveySection: e.target.value })} />
                      </div>
                      <div>
                        <label className="text-xs text-text-muted">Survey Label</label>
                        <Input size="sm" value={col.surveyLabel || ''} onChange={(e) => updateColumn(i, { surveyLabel: e.target.value })} />
                      </div>
                      <label className="flex items-center gap-1 text-xs pt-4">
                        <input type="checkbox" checked={col.isDemographic || false} onChange={(e) => updateColumn(i, { isDemographic: e.target.checked })} />
                        Demographic (exclude from metrics)
                      </label>
                    </div>

                    {/* Type-specific fields */}
                    {(col.type === 'number' || col.type === 'scale') && (
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs text-text-muted">Min</label>
                          <Input size="sm" type="number" value={col.min ?? ''} onChange={(e) => updateColumn(i, { min: e.target.value ? Number(e.target.value) : undefined })} />
                        </div>
                        <div>
                          <label className="text-xs text-text-muted">Max</label>
                          <Input size="sm" type="number" value={col.max ?? ''} onChange={(e) => updateColumn(i, { max: e.target.value ? Number(e.target.value) : undefined })} />
                        </div>
                        <div>
                          <label className="text-xs text-text-muted">Step</label>
                          <Input size="sm" type="number" value={col.step ?? ''} onChange={(e) => updateColumn(i, { step: e.target.value ? Number(e.target.value) : undefined })} />
                        </div>
                      </div>
                    )}

                    {(col.type === 'enum' || col.type === 'multi-enum') && (
                      <div>
                        <label className="text-xs text-text-muted">Options (comma-separated)</label>
                        <Input
                          size="sm"
                          value={(col.options || []).join(', ')}
                          onChange={(e) => updateColumn(i, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                          placeholder="Option1, Option2, Option3"
                        />
                      </div>
                    )}

                    {col.type === 'likert-group' && (
                      <div className="space-y-2">
                        <div>
                          <label className="text-xs text-text-muted">Sub-fields (one per line: key, label)</label>
                          <textarea
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-brand-primary"
                            rows={3}
                            value={(col.subFields || []).map(sf => `${sf.key}, ${sf.label}`).join('\n')}
                            onChange={(e) => {
                              const subs = e.target.value.split('\n').filter(Boolean).map(line => {
                                const [key, ...rest] = line.split(',').map(s => s.trim());
                                return { key: key || '', label: rest.join(', ') || key || '' };
                              });
                              updateColumn(i, { subFields: subs });
                            }}
                            placeholder="q1, I enjoy using technology&#10;q2, I find technology easy"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-text-muted">Likert options (one per line: value, label)</label>
                          <textarea
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-brand-primary"
                            rows={3}
                            value={(col.likertOptions || []).map(lo => `${lo.value}, ${lo.label}`).join('\n')}
                            onChange={(e) => {
                              const opts = e.target.value.split('\n').filter(Boolean).map(line => {
                                const [val, ...rest] = line.split(',').map(s => s.trim());
                                return { value: Number(val) || 0, label: rest.join(', ') || val || '' };
                              });
                              updateColumn(i, { likertOptions: opts });
                            }}
                            placeholder="1, Completely Disagree&#10;2, Disagree&#10;3, Neutral"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {kind === 'paired' && (
        <Card>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-semibold">Column Mapping</h3>
            {idColumn && (
              <span className="text-xs text-text-muted">
                ID: <span className="font-mono">{idColumn.preKey} ↔ {idColumn.postKey}</span>
              </span>
            )}
          </div>
          <p className="text-xs text-text-muted mb-3">
            Drag to reorder. Each mapping pairs a Pre column with a Post column. Mark one mapping as the ID column to join responses.
          </p>
          <div className="mb-4">
            <p className="text-xs font-medium text-text-secondary-light dark:text-text-secondary mb-2">Respondent matching</p>
            <div className="flex gap-2">
              {[
                { value: 'cookie', label: 'Auto (cookie)', description: 'Same device/browser auto-matched — no input needed' },
                { value: 'manual', label: 'Manual code', description: 'Respondent types a matching code in both surveys' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setMatchingStrategy(opt.value)}
                  className={`flex-1 text-left px-3 py-2 rounded-lg border text-sm transition-colors cursor-pointer ${
                    matchingStrategy === opt.value
                      ? 'border-brand-primary bg-brand-primary/5 text-text-primary-light dark:text-text-primary'
                      : 'border-border-light dark:border-border text-text-secondary-light dark:text-text-secondary hover:border-brand-primary/50'
                  }`}
                >
                  <span className="font-medium block">{opt.label}</span>
                  <span className="text-xs text-text-muted-light dark:text-text-muted">{opt.description}</span>
                </button>
              ))}
            </div>
          </div>
          <ColumnMappingList
            mappings={columnMapping}
            setMappings={setColumnMapping}
            preColumns={preColumns}
            postColumns={postColumns}
            idColumn={idColumn}
            setIdColumn={setIdColumn}
          />
        </Card>
      )}

      {/* Metric Builder */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold">Metrics</h3>
          <Button variant="primary" size="sm" onClick={() => setEffectiveMetrics([...effectiveMetrics, makeEmptyMetric()])}>
            <PlusIcon className="w-4 h-4 mr-1 inline" />
            Add Metric
          </Button>
        </div>

        {effectiveMetrics.length === 0 ? (
          <div className="text-center py-8 text-text-muted-light dark:text-text-muted text-sm">
            No metrics defined. Add metrics to compute from column data.
          </div>
        ) : (
          <div className="space-y-2">
            {effectiveMetrics.map((metric, i) => (
              <div key={i} className="border border-border rounded-lg">
                <div
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                  onClick={() => setExpandedMetric(expandedMetric === i ? null : i)}
                >
                  <input
                    className="flex-1 text-sm bg-transparent border-none outline-none text-text-primary-light dark:text-text-primary"
                    value={metric.key}
                    placeholder="key"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updateMetric(i, { key: e.target.value })}
                  />
                  <input
                    className="flex-1 text-sm bg-transparent border-none outline-none text-text-secondary-light dark:text-text-secondary"
                    value={metric.label}
                    placeholder="Label"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updateMetric(i, { label: e.target.value })}
                  />
                  <select
                    className="text-xs bg-transparent border border-border rounded px-1 py-0.5 text-text-secondary-light dark:text-text-secondary"
                    value={metric.formula}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updateMetric(i, { formula: e.target.value })}
                  >
                    {FORMULA_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <button onClick={(e) => { e.stopPropagation(); removeMetric(i); }} className="p-0.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors">
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                </div>

                {expandedMetric === i && (
                  <div className="px-3 py-3 border-t border-border bg-neutral-50 dark:bg-neutral-900/50 space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-text-muted">Unit</label>
                        <Input size="sm" value={metric.unit || ''} onChange={(e) => updateMetric(i, { unit: e.target.value })} placeholder="%, /5, /100" />
                      </div>
                      <div>
                        <label className="text-xs text-text-muted">Decimals</label>
                        <Input size="sm" type="number" value={metric.decimals ?? 1} onChange={(e) => updateMetric(i, { decimals: Number(e.target.value) })} />
                      </div>
                      <div>
                        <label className="text-xs text-text-muted">Display Scale (max)</label>
                        <Input size="sm" type="number" value={metric.displayScale?.max ?? 100} onChange={(e) => updateMetric(i, { displayScale: { ...metric.displayScale, max: Number(e.target.value) } })} />
                      </div>
                    </div>

                    {/* Formula-specific config */}
                    {(metric.formula === 'average' || metric.formula === 'normalize-sum' || metric.formula === 'average-mapped') && (
                      <div>
                        <label className="text-xs text-text-muted">Source Column</label>
                        <Select size="sm" value={metric.sourceColumn || ''} onChange={(e) => updateMetric(i, { sourceColumn: e.target.value })}>
                          <option value="">Select column...</option>
                          {columnKeys.map(k => <option key={k} value={k}>{k}</option>)}
                        </Select>
                      </div>
                    )}

                    {metric.formula === 'normalize-sum' && (
                      <div className="grid grid-cols-4 gap-3">
                        <div>
                          <label className="text-xs text-text-muted">From Min</label>
                          <Input size="sm" type="number" value={metric.normalizeFrom?.min ?? ''} onChange={(e) => updateMetric(i, { normalizeFrom: { ...metric.normalizeFrom, min: Number(e.target.value) } })} />
                        </div>
                        <div>
                          <label className="text-xs text-text-muted">From Max</label>
                          <Input size="sm" type="number" value={metric.normalizeFrom?.max ?? ''} onChange={(e) => updateMetric(i, { normalizeFrom: { ...metric.normalizeFrom, max: Number(e.target.value) } })} />
                        </div>
                        <div>
                          <label className="text-xs text-text-muted">To Min</label>
                          <Input size="sm" type="number" value={metric.normalizeTo?.min ?? 0} onChange={(e) => updateMetric(i, { normalizeTo: { ...metric.normalizeTo, min: Number(e.target.value) } })} />
                        </div>
                        <div>
                          <label className="text-xs text-text-muted">To Max</label>
                          <Input size="sm" type="number" value={metric.normalizeTo?.max ?? 100} onChange={(e) => updateMetric(i, { normalizeTo: { ...metric.normalizeTo, max: Number(e.target.value) } })} />
                        </div>
                      </div>
                    )}

                    {metric.formula === 'average-mapped' && (
                      <div>
                        <label className="text-xs text-text-muted">Value Map (one per line: option, value)</label>
                        <textarea
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-brand-primary"
                          rows={4}
                          value={metric.valueMap ? Object.entries(metric.valueMap).map(([k, v]) => `${k}, ${v}`).join('\n') : ''}
                          onChange={(e) => {
                            const map = {};
                            e.target.value.split('\n').filter(Boolean).forEach(line => {
                              const [key, val] = line.split(',').map(s => s.trim());
                              if (key) map[key] = Number(val) || 0;
                            });
                            updateMetric(i, { valueMap: map });
                          }}
                          placeholder="Daily, 100&#10;Weekly, 75&#10;Monthly, 50&#10;Never, 0"
                        />
                      </div>
                    )}

                    {(metric.formula === 'percentage-where' || metric.formula === 'count-where') && (
                      <div>
                        <label className="text-xs text-text-muted">Condition (JSON: {`{ "field": "...", "op": "==", "value": ... }`})</label>
                        <textarea
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-brand-primary font-mono"
                          rows={3}
                          value={metric.condition ? JSON.stringify(metric.condition, null, 2) : ''}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              updateMetric(i, { condition: parsed });
                            } catch {
                              // Allow typing incomplete JSON
                            }
                          }}
                          placeholder='{ "field": "awareOfGenAI", "op": "==", "value": true }'
                        />
                      </div>
                    )}

                    {metric.formula === 'expression' && (
                      <div>
                        <label className="text-xs text-text-muted">Expression</label>
                        <Input
                          size="sm"
                          value={metric.expression || ''}
                          onChange={(e) => updateMetric(i, { expression: e.target.value })}
                          placeholder="(avg(benefitHumankind) - avg(destroyHumankind) + 10) / 2"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Dashboard Layout */}
      <Card>
        <h3 className="text-base font-semibold mb-4">Dashboard Layout</h3>
        {metricKeys.length === 0 ? (
          <p className="text-sm text-text-muted">Define metrics first to configure the dashboard layout.</p>
        ) : (
          <div>
            <p className="text-xs text-text-muted mb-3">Toggle which dashboard areas each metric appears in.</p>
            <div className="border border-border rounded-lg overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-[1fr_repeat(4,72px)] gap-0 px-3 py-2 bg-neutral-50 dark:bg-neutral-900/50 border-b border-border text-[11px] font-medium uppercase tracking-wider text-text-muted">
                <span>Metric</span>
                <span className="text-center">Cards</span>
                <span className="text-center">Series</span>
                <span className="text-center">Table</span>
                <span className="text-center">Radar</span>
              </div>
              {/* Rows */}
              {metricKeys.map(key => {
                const label = effectiveMetrics.find(m => m.key === key)?.label || key;
                return (
                  <div key={key} className="grid grid-cols-[1fr_repeat(4,72px)] gap-0 px-3 py-1.5 border-b border-border last:border-b-0 items-center hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                    <span className="text-sm text-text-primary-light dark:text-text-primary truncate">{label}</span>
                    {['overviewCards', 'timeSeriesMetrics', 'comparisonMetrics', 'radarMetrics'].map(section => (
                      <div key={section} className="flex justify-center">
                        <button
                          onClick={() => toggleLayoutMetric(section, key)}
                          className={`w-5 h-5 rounded border transition-colors cursor-pointer flex items-center justify-center ${
                            isLayoutMetricSelected(section, key)
                              ? 'bg-brand-primary border-brand-primary text-white'
                              : 'border-border hover:border-brand-primary'
                          }`}
                        >
                          {isLayoutMetricSelected(section, key) && (
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                            </svg>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      {/* MaxDiff Charts */}
      <Card>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold">MaxDiff Charts</h3>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setLayout(prev => ({
              ...prev,
              maxDiffCharts: [
                ...(prev.maxDiffCharts || []),
                { key: `maxdiff${(prev.maxDiffCharts || []).length + 1}`, label: 'New MaxDiff', bestColumn: '', worstColumn: '', color: DEFAULT_COLORS[(prev.maxDiffCharts || []).length % DEFAULT_COLORS.length], side: kind === 'paired' ? 'pre' : undefined },
              ],
            }))}
          >
            <PlusIcon className="w-4 h-4 mr-1 inline" />
            Add MaxDiff Chart
          </Button>
        </div>
        <p className="text-xs text-text-muted mb-3">
          Aggregates two enum columns (e.g. "biggest concern" vs "least concern") into a net-score bar chart.
        </p>
        {(layout.maxDiffCharts || []).length === 0 ? (
          <div className="text-center py-6 text-text-muted text-sm">
            No MaxDiff charts configured.
          </div>
        ) : (
          <div className="space-y-2">
            {(layout.maxDiffCharts || []).map((chart, i) => {
              const sourceColumns = kind === 'paired'
                ? (chart.side === 'post' ? postColumns : preColumns)
                : columns;
              const enumCols = sourceColumns.filter(c => c.type === 'enum' && c.key);
              return (
                <div key={i} className="border border-border rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      className="flex-1 text-sm bg-transparent border-b border-border outline-none text-text-primary-light dark:text-text-primary"
                      value={chart.label}
                      placeholder="Chart label"
                      onChange={(e) => setLayout(prev => ({
                        ...prev,
                        maxDiffCharts: prev.maxDiffCharts.map((c, j) => j === i ? { ...c, label: e.target.value, key: c.key || `maxdiff${i + 1}` } : c),
                      }))}
                    />
                    <input
                      type="color"
                      value={chart.color || '#64cc9c'}
                      className="w-8 h-8 rounded cursor-pointer border border-border"
                      onChange={(e) => setLayout(prev => ({
                        ...prev,
                        maxDiffCharts: prev.maxDiffCharts.map((c, j) => j === i ? { ...c, color: e.target.value } : c),
                      }))}
                    />
                    <button
                      onClick={() => setLayout(prev => ({
                        ...prev,
                        maxDiffCharts: prev.maxDiffCharts.filter((_, j) => j !== i),
                      }))}
                      className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {kind === 'paired' && (
                      <div>
                        <label className="text-xs text-text-muted">Side</label>
                        <Select
                          size="sm"
                          value={chart.side || 'pre'}
                          onChange={(e) => setLayout(prev => ({
                            ...prev,
                            maxDiffCharts: prev.maxDiffCharts.map((c, j) => j === i ? { ...c, side: e.target.value, bestColumn: '', worstColumn: '' } : c),
                          }))}
                        >
                          <option value="pre">Pre</option>
                          <option value="post">Post</option>
                        </Select>
                      </div>
                    )}
                    <div>
                      <label className="text-xs text-text-muted">Best / Most Column</label>
                      <Select
                        size="sm"
                        value={chart.bestColumn || ''}
                        onChange={(e) => setLayout(prev => ({
                          ...prev,
                          maxDiffCharts: prev.maxDiffCharts.map((c, j) => j === i ? { ...c, bestColumn: e.target.value } : c),
                        }))}
                      >
                        <option value="">Select column...</option>
                        {enumCols.map(c => <option key={c.key} value={c.key}>{c.label || c.key}</option>)}
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-text-muted">Worst / Least Column</label>
                      <Select
                        size="sm"
                        value={chart.worstColumn || ''}
                        onChange={(e) => setLayout(prev => ({
                          ...prev,
                          maxDiffCharts: prev.maxDiffCharts.map((c, j) => j === i ? { ...c, worstColumn: e.target.value } : c),
                        }))}
                      >
                        <option value="">Select column...</option>
                        {enumCols.map(c => <option key={c.key} value={c.key}>{c.label || c.key}</option>)}
                      </Select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Bottom action bar */}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Update Database' : 'Create Database'}
        </Button>
      </div>
    </div>
  );
}
